import React from 'react';
import {
  Autocomplete,
  Chip,
  MenuItem,
  Stack,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import { GhostButton } from './primitives';

/**
 * Shared pickers for admin fields whose value is a REFERENCE to another object (a capability key, a
 * content-block id, an asset kind, a role slot) or a fixed enum — the things that used to be free text
 * and silently broke a runtime link on a typo. Two variants, both matching the builder's small MUI look:
 *
 *  - `ReferenceSelect` — a single-value dropdown.
 *  - `ReferenceMultiSelect` — a chips multi-select.
 *
 * Both PRESERVE an unknown/stale stored value (rendered "(missing)") so repointing never silently drops
 * a value the options list no longer contains — the same guard the Asset picker already uses
 * (`StepFilesFields.tsx`). Pass `onCreate` to offer a "+ New" affordance that stub-creates the related
 * object and selects it (mirroring the Asset create-and-reference pattern); omit it for a plain enum.
 */

export interface ReferenceOption {
  value: string;
  label: string;
  /** Optional secondary text shown after the label (e.g. the underlying key). */
  hint?: string;
}

/** Sentinel option value used to trigger `onCreate` from the single-select menu. */
const CREATE_SENTINEL = '__ref_create__';

export function ReferenceSelect(props: {
  label: string;
  value: string;
  options: ReferenceOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  helperText?: React.ReactNode;
  /** When set, renders a blank option with this label at the top (e.g. "None / always show"). */
  emptyLabel?: string;
  /** Stub-creates a related object and resolves to its key (or null on failure); adds a "+ New" item. */
  onCreate?: () => Promise<string | null>;
  createLabel?: string;
  sx?: object;
  minWidth?: number;
  fullWidth?: boolean;
}): JSX.Element {
  const {
    label,
    value,
    options,
    onChange,
    disabled,
    helperText,
    emptyLabel,
    onCreate,
    createLabel,
    sx,
    minWidth,
    fullWidth,
  } = props;
  const known = value === '' || options.some((o) => o.value === value);

  return (
    <TextField
      select
      label={label}
      size="small"
      value={value}
      disabled={disabled}
      helperText={helperText}
      fullWidth={fullWidth}
      onChange={async (e) => {
        if (e.target.value === CREATE_SENTINEL) {
          const created = onCreate ? await onCreate() : null;
          if (created) onChange(created);
          return;
        }
        onChange(e.target.value);
      }}
      sx={{ minWidth: fullWidth ? undefined : minWidth ?? 200, ...(sx ?? {}) }}
    >
      {emptyLabel !== undefined && (
        <MenuItem value="">
          <em>{emptyLabel}</em>
        </MenuItem>
      )}
      {/* Preserve an unknown/stale value so repointing never silently drops it. */}
      {value !== '' && !known && <MenuItem value={value}>{value} (missing)</MenuItem>}
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
          {o.hint ? ` · ${o.hint}` : ''}
        </MenuItem>
      ))}
      {onCreate && <MenuItem value={CREATE_SENTINEL}>{createLabel ?? '+ New'}</MenuItem>}
    </TextField>
  );
}

export function ReferenceMultiSelect(props: {
  label: string;
  value: string[];
  options: ReferenceOption[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  helperText?: React.ReactNode;
  /** Stub-creates a related object and resolves to its key (or null on failure); adds a "+ New" button. */
  onCreate?: () => Promise<string | null>;
  createLabel?: string;
}): JSX.Element {
  const { label, value, options, onChange, disabled, helperText, onCreate, createLabel } = props;

  // Surface unknown/stale stored values as selectable options so they render (and aren't dropped on save).
  const optByValue = new Map(options.map((o) => [o.value, o]));
  const merged: ReferenceOption[] = [
    ...options,
    ...value.filter((v) => !optByValue.has(v)).map((v) => ({ value: v, label: `${v} (missing)` })),
  ];
  const selected = merged.filter((o) => value.includes(o.value));

  return (
    <Stack spacing={0.75} sx={{ flex: 1 }}>
      <Autocomplete
        multiple
        size="small"
        disabled={disabled}
        options={merged}
        value={selected}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        getOptionLabel={(o) => o.label}
        onChange={(_e, next) => onChange(next.map((o) => o.value))}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => (
            <Chip size="small" label={tag.label} {...getTagProps({ index })} key={tag.value + index} />
          ))
        }
        renderInput={(params) => <TextField {...params} label={label} helperText={helperText} />}
      />
      {onCreate && !disabled && (
        <GhostButton
          onClick={async () => {
            const created = await onCreate();
            if (created && !value.includes(created)) onChange([...value, created]);
          }}
        >
          {createLabel ?? '+ New'}
        </GhostButton>
      )}
    </Stack>
  );
}
